(() => {
  const CHECKED_CLASSES = ['border-[#0A0A0A]', 'bg-[#0A0A0A]', 'text-white'];
  const UNCHECKED_CLASSES = ['border-black/20', 'bg-white', 'text-transparent'];

  const themeRoutes = () => {
    if (typeof Theme !== 'undefined' && Theme.routes) return Theme.routes;
    return window.Theme?.routes;
  };

  const formatMoney = (section, cents) => {
    const value = Number(cents || 0) / 100;
    return new Intl.NumberFormat(document.documentElement.lang || undefined, {
      style: 'currency',
      currency: section.dataset.currency || 'USD',
    }).format(value);
  };

  const setToggle = (button, checked) => {
    if (!button || button.disabled) return;
    button.setAttribute('aria-pressed', checked ? 'true' : 'false');
    button.classList.remove(...CHECKED_CLASSES, ...UNCHECKED_CLASSES);
    button.classList.add(...(checked ? CHECKED_CLASSES : UNCHECKED_CLASSES));
    button.querySelector('svg')?.classList.toggle('opacity-0', !checked);
  };

  const cartSectionIds = () => {
    const ids = [];
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) ids.push(item.dataset.sectionId);
    });
    return ids;
  };

  const updateHeaderCart = async () => {
    const routes = themeRoutes();
    if (!routes?.cart_url) return null;

    const cart = await fetch(`${routes.cart_url}.js`).then((response) => response.json());
    document.querySelectorAll('.cart-bubble__text-count').forEach((count) => {
      count.textContent = String(cart.item_count || 0);
      count.classList.toggle('hidden', !cart.item_count);
    });
    document.querySelectorAll('.cart-bubble').forEach((bubble) => {
      bubble.classList.toggle('visually-hidden', !cart.item_count);
    });
    document.querySelectorAll('cart-icon').forEach((icon) => {
      icon.classList.toggle('header-actions__cart-icon--has-cart', cart.item_count > 0);
    });
    return cart;
  };

  const dispatchCartUpdate = (section, cart, sourceId, sections) => {
    if (!cart) return;
    document.dispatchEvent(new CustomEvent('cart:update', {
      bubbles: true,
      detail: {
        resource: cart,
        sourceId,
        data: {
          source: 'lumppings-product-hero',
          itemCount: cart.item_count || 0,
          productId: section.dataset.productId,
          sections,
        },
      },
    }));
  };

  const openCartNotification = () => {
    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer && typeof cartDrawer.open === 'function') {
      cartDrawer.open();
      return;
    }

    const cartTrigger = document.querySelector('[data-testid="cart-drawer-trigger"]');
    if (cartTrigger instanceof HTMLElement) cartTrigger.click();
  };

  const addLineItems = async (section, items, sourceId) => {
    const payloadItems = items
      .map((item) => ({ id: Number(item.id), quantity: Number(item.quantity || 1) }))
      .filter((item) => item.id && item.quantity > 0);

    const routes = themeRoutes();
    if (!payloadItems.length || !routes?.cart_add_url) return null;

    const response = await fetch(routes.cart_add_url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        items: payloadItems,
        sections: cartSectionIds().join(','),
      }),
    });
    const payload = await response.json();
    if (!response.ok || payload.status) {
      throw new Error(payload.description || payload.message || 'Add to cart failed');
    }

    const cart = await updateHeaderCart();
    dispatchCartUpdate(section, cart, sourceId, payload.sections);
    openCartNotification();
    return cart;
  };

  const initMediaGallery = (section) => {
    const frames = [...section.querySelectorAll('[data-product-main-frame]')];
    const thumbs = [...section.querySelectorAll('[data-product-thumb]')];

    const setProductMediaToggleState = (button, isPlaying) => {
      if (!button) return;
      button.dataset.playing = isPlaying ? 'true' : 'false';
      button.setAttribute('aria-label', isPlaying ? 'Pause product video' : 'Play product video');
    };

    const getProductEmbedProvider = (iframe) => {
      try {
        const url = new URL(iframe.src, window.location.href);
        const hostname = url.hostname.replace(/^www\./, '').replace(/^m\./, '');
        if (hostname === 'youtube.com' || hostname === 'youtube-nocookie.com') return 'youtube';
        if (hostname === 'player.vimeo.com' || hostname === 'vimeo.com') return 'vimeo';
      } catch (error) {
        return '';
      }

      return '';
    };

    const prepareProductEmbed = (iframe, provider) => {
      if (!provider || iframe.dataset.productEmbedPrepared === 'true') return;

      try {
        const url = new URL(iframe.src, window.location.href);
        if (provider === 'youtube') {
          url.searchParams.set('enablejsapi', '1');
          url.searchParams.set('controls', '0');
          url.searchParams.set('playsinline', '1');
          url.searchParams.set('rel', '0');
          url.searchParams.set('modestbranding', '1');
        } else if (provider === 'vimeo') {
          url.searchParams.set('api', '1');
          url.searchParams.set('controls', '0');
        }
        iframe.src = url.toString();
        iframe.dataset.productEmbedPrepared = 'true';
      } catch (error) {
        iframe.dataset.productEmbedPrepared = 'true';
      }
    };

    const postProductEmbedCommand = (iframe, provider, command) => {
      if (!iframe.contentWindow) return;

      if (provider === 'youtube') {
        iframe.contentWindow.postMessage(JSON.stringify({
          event: 'command',
          func: command === 'play' ? 'playVideo' : 'pauseVideo',
          args: [],
        }), '*');
      } else if (provider === 'vimeo') {
        iframe.contentWindow.postMessage(JSON.stringify({
          method: command,
        }), '*');
      }
    };

    const bindProductVideoControls = (frame) => {
      const video = frame.querySelector('video');
      const iframe = frame.querySelector('iframe');
      const toggle = frame.querySelector('[data-product-media-toggle]');
      if (!toggle || toggle.dataset.productMediaBound === 'true') return;

      toggle.dataset.productMediaBound = 'true';
      if (video) {
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          if (video.paused) {
            video.play();
          } else {
            video.pause();
          }
        });

        video.addEventListener('play', () => setProductMediaToggleState(toggle, true));
        video.addEventListener('pause', () => setProductMediaToggleState(toggle, false));
        video.addEventListener('ended', () => setProductMediaToggleState(toggle, false));
        setProductMediaToggleState(toggle, !video.paused);
        return;
      }

      if (iframe) {
        const provider = getProductEmbedProvider(iframe);
        prepareProductEmbed(iframe, provider);
        toggle.addEventListener('click', (event) => {
          event.preventDefault();
          event.stopPropagation();

          const nextPlaying = toggle.dataset.playing !== 'true';
          setProductMediaToggleState(toggle, nextPlaying);
          postProductEmbedCommand(iframe, provider, nextPlaying ? 'play' : 'pause');
        });
      }
    };

    const pauseFrameMedia = (frame) => {
      frame.querySelectorAll('video').forEach((video) => video.pause());
      frame.querySelectorAll('[data-product-media-toggle]').forEach((button) => setProductMediaToggleState(button, false));
      frame.querySelectorAll('iframe').forEach((iframe) => {
        const provider = getProductEmbedProvider(iframe);
        if (provider) postProductEmbedCommand(iframe, provider, 'pause');
        iframe.src = iframe.src;
      });
    };

    const setMediaFrame = (index) => {
      thumbs.forEach((thumb, thumbIndex) => {
        const preview = thumb.firstElementChild;
        const active = thumbIndex === index;
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (!preview) return;
        preview.style.outline = active ? '2px solid #3C94F3' : '';
        preview.style.outlineOffset = active ? '3px' : '';
      });

      frames.forEach((frame, frameIndex) => {
        const active = frameIndex === index;
        frame.classList.toggle('hidden', !active);
        frame.setAttribute('aria-hidden', active ? 'false' : 'true');
        if (!active) pauseFrameMedia(frame);
      });
    };

    thumbs.forEach((thumb, index) => {
      thumb.style.cursor = 'pointer';
      thumb.addEventListener('click', () => setMediaFrame(index));
      thumb.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        setMediaFrame(index);
      });
    });
    frames.forEach(bindProductVideoControls);
    if (thumbs.length && frames.length) setMediaFrame(0);
  };

  const selectedAddonSlides = (section) => [...section.querySelectorAll('[data-addon-slide]')].filter((slide) => {
    const toggle = slide.querySelector('[data-addon-toggle]');
    return toggle?.getAttribute('aria-pressed') === 'true';
  });

  const addonLineItems = (section) => selectedAddonSlides(section)
    .map((slide) => ({ id: Number(slide.dataset.variantId), quantity: 1 }))
    .filter((item) => item.id);

  const updateAddonPriceNotes = (section) => {
    const selectedAddons = selectedAddonSlides(section);
    const addonTotal = selectedAddons.reduce((sum, slide) => sum + Number(slide.dataset.price || 0), 0);

    section.querySelectorAll('[data-addon-price-note]').forEach((note) => {
      if (!addonTotal) {
        note.hidden = true;
        note.textContent = '';
        return;
      }

      const productPrice = Number(note.dataset.productPrice || 0);
      const total = productPrice + addonTotal;
      const variables = {
        'product price': formatMoney(section, productPrice),
        'addons total': formatMoney(section, addonTotal),
        'product price + selected add ons': formatMoney(section, total),
        total: formatMoney(section, total),
      };

      let text = note.dataset.template || '';
      Object.entries(variables).forEach(([key, value]) => {
        text = text.replaceAll(`[${key}]`, value);
      });
      note.textContent = text;
      note.hidden = false;
    });
  };

  const initAddonSlider = (section, slider) => {
    const track = slider.querySelector('[data-addon-track]');
    const slides = [...slider.querySelectorAll('[data-addon-slide]')];
    const previous = slider.querySelector('[data-addon-prev]');
    const next = slider.querySelector('[data-addon-next]');
    let activeSlide = 0;

    const showSlide = (index) => {
      if (!track || !slides.length) return;
      activeSlide = (index + slides.length) % slides.length;
      track.style.transform = `translateX(-${activeSlide * 100}%)`;
      previous?.toggleAttribute('disabled', slides.length < 2);
      next?.toggleAttribute('disabled', slides.length < 2);
    };

    slides.forEach((slide) => {
      const card = slide.querySelector('[data-addon-card]');
      const toggle = card?.querySelector('[data-addon-toggle]');
      const update = (checked) => {
        setToggle(toggle, checked);
        card?.classList.toggle('opacity-70', !checked);
      };

      card?.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        event.preventDefault();
        update(toggle?.getAttribute('aria-pressed') !== 'true');
        updateAddonPriceNotes(section);
      });
      update(toggle?.getAttribute('aria-pressed') === 'true');
    });

    previous?.addEventListener('click', () => showSlide(activeSlide - 1));
    next?.addEventListener('click', () => showSlide(activeSlide + 1));
    showSlide(activeSlide);
  };

  const addMainProductWithAddons = async (section, form) => {
    if (!(form instanceof HTMLFormElement)) return;

    const addons = addonLineItems(section);
    if (!addons.length || !themeRoutes()?.cart_add_url) return;

    const submit = form.querySelector('[type="submit"]');
    const variantInput = form.querySelector('[name="id"]');
    const quantityInput = form.querySelector('[name="quantity"]');
    const mainVariantId = Number(variantInput?.value);
    const mainQuantity = Number(quantityInput?.value || 1);

    if (submit) submit.disabled = true;
    try {
      await addLineItems(section, [
        { id: mainVariantId, quantity: mainQuantity > 0 ? mainQuantity : 1 },
        ...addons,
      ], form.id || section.dataset.lumppingsProductHero);
    } catch (error) {
      console.error(error);
    } finally {
      if (submit) submit.disabled = false;
    }
  };

  const initAddonCartInterception = (section) => {
    window.addEventListener('click', (event) => {
      const submit = event.target?.closest?.('product-form-component [type="submit"]');
      const form = submit?.closest?.('form');
      if (!submit || !form || !section.contains(submit) || !addonLineItems(section).length) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      addMainProductWithAddons(section, form);
    }, true);

    window.addEventListener('submit', async (event) => {
      const form = event.target;
      if (!(form instanceof HTMLFormElement) || !section.contains(form) || !form.closest('product-form-component')) return;
      if (!addonLineItems(section).length || !themeRoutes()?.cart_add_url) return;

      event.preventDefault();
      event.stopImmediatePropagation();
      await addMainProductWithAddons(section, form);
    }, true);
  };

  const initBundle = (section, bundle) => {
    const items = [...bundle.querySelectorAll('[data-bundle-item]')];
    const submit = bundle.querySelector('[data-bundle-submit]');
    const total = bundle.querySelector('[data-bundle-total]');
    const compareTotal = bundle.querySelector('[data-bundle-compare-total]');

    const selectedItems = () => items.filter((item) => {
      const toggle = item.querySelector('[data-bundle-toggle]');
      return item.dataset.required === 'true' || toggle?.getAttribute('aria-pressed') === 'true';
    });

    const updateBundle = () => {
      let price = 0;
      let compare = 0;
      selectedItems().forEach((item) => {
        price += Number(item.dataset.price || 0);
        compare += Number(item.dataset.comparePrice || item.dataset.price || 0);
      });
      if (total) total.textContent = formatMoney(section, price);
      if (compareTotal) compareTotal.textContent = compare > price ? formatMoney(section, compare) : '';
    };

    items.forEach((item) => {
      const toggle = item.querySelector('[data-bundle-toggle]');
      if (!toggle || item.dataset.required === 'true') return;
      item.addEventListener('click', (event) => {
        if (event.target.closest('a')) return;
        event.preventDefault();
        setToggle(toggle, toggle.getAttribute('aria-pressed') !== 'true');
        item.classList.toggle('opacity-70', toggle.getAttribute('aria-pressed') !== 'true');
        updateBundle();
      });
    });

    submit?.addEventListener('click', async () => {
      const payloadItems = selectedItems()
        .map((item) => ({ id: Number(item.dataset.variantId), quantity: 1 }))
        .filter((item) => item.id);
      if (!payloadItems.length || !themeRoutes()?.cart_add_url) return;

      submit.disabled = true;
      try {
        await addLineItems(section, payloadItems, bundle.dataset.productBundle || section.dataset.lumppingsProductHero);
      } catch (error) {
        console.error(error);
      } finally {
        submit.disabled = false;
      }
    });

    updateBundle();
  };

  const initReviewSlider = (reviewRoot) => {
    const slides = [...reviewRoot.querySelector('.overflow-hidden')?.children || []];
    const dots = [...reviewRoot.querySelectorAll('[aria-label] button')].slice(-3);
    let activeReview = Math.max(0, slides.findIndex((slide) => slide.classList.contains('block')));

    const showReview = (index) => {
      if (!slides.length) return;
      activeReview = (index + slides.length) % slides.length;
      slides.forEach((slide, slideIndex) => {
        slide.classList.toggle('block', slideIndex === activeReview);
        slide.classList.toggle('hidden', slideIndex !== activeReview);
      });
      dots.forEach((dot, dotIndex) => {
        const active = dotIndex === activeReview;
        dot.toggleAttribute('aria-current', active);
        dot.classList.toggle('w-6', active);
        dot.classList.toggle('w-2.5', !active);
        dot.classList.toggle('bg-[#0A0A0A]', active);
        dot.classList.toggle('bg-black/20', !active);
        dot.classList.toggle('hover:bg-black/30', !active);
      });
    };

    reviewRoot.querySelectorAll('[aria-label="Previous review"]').forEach((button) => {
      button.addEventListener('click', () => showReview(activeReview - 1));
    });
    reviewRoot.querySelectorAll('[aria-label="Next review"]').forEach((button) => {
      button.addEventListener('click', () => showReview(activeReview + 1));
    });
    dots.forEach((dot, index) => dot.addEventListener('click', () => showReview(index)));
    showReview(activeReview);
  };

  const initProductHero = (section) => {
    if (section.dataset.lumppingsProductHeroReady === 'true') return;
    section.dataset.lumppingsProductHeroReady = 'true';

    initMediaGallery(section);
    section.querySelectorAll('[data-product-addon-slider]').forEach((slider) => initAddonSlider(section, slider));
    updateAddonPriceNotes(section);
    initAddonCartInterception(section);
    section.querySelectorAll('[data-product-bundle]').forEach((bundle) => initBundle(section, bundle));
    section.querySelectorAll('[data-review-slider]').forEach(initReviewSlider);
  };

  document.querySelectorAll('[data-lumppings-product-hero]').forEach(initProductHero);
})();
